// Reine, DB-freie Logik fuer die Lager-Reichweite-Seite (Task 17,
// pages/analytics/InventoryReach.tsx). Getrennt von der Seite gehalten, damit die
// Dringlichkeits-Einstufung und der CSV-Aufbau ohne React/DOM testbar sind — Konvention
// wie lib/article-analysis.ts (Task 16) und lib/dash-format.ts (Task 13/14).
//
// Spiegel der Backend-Semantik aus src/modules/dashboard/service.ts (buildReachSql,
// reachExpr): reach_months ist NULL, wenn im gewaehlten Zeitfenster kein (positiver)
// Absatz vorlag (avg_month <= 0) — UNABHAENGIG vom aktuellen Bestand. Ist der Bestand
// bereits <= 0, liefert das Backend reach_months = 0 (nicht NULL) — das ist der
// dringendste Fall ("jetzt schon leer"), keine eigene Kategorie noetig: 0 faellt bereits
// unter die "kritisch"-Schwelle unten.
import type { ReachRow, ReachOpenNote, Supplier, ReachOpenPurchase, ReachBausatzTeil } from "@/api/dashboard";
import { escapeCsvField, formatCsvDecimal } from "./csv";

// Nachlieferzeit lt. Fachvorgabe (Task-17-Brief): ca. 6 Monate. Ein Artikel, der
// innerhalb dieser Frist leerlaeuft, kann durch eine JETZT ausgeloeste Bestellung nicht
// mehr rechtzeitig aufgefuellt werden — das ist die "kritisch"-Schwelle. Der zweite
// Schwellwert (12 Monate) markiert "bald, aber noch innerhalb einer Nachbestell-Zyklus-
// Reserve" — beide Werte sind bewusst als benannte Konstanten gehalten (nicht als
// Formel wie "2x Nachlieferzeit"), weil sie unabhaengig voneinander gepflegt werden
// koennen sollen, falls sich die Fachvorgabe fuer eine der beiden Schwellen aendert.
export const REORDER_LEAD_MONTHS = 6;
export const WARNING_HORIZON_MONTHS = 12;

export type ReachUrgency = "critical" | "warning" | "ok" | "no_sales";

export interface ReachUrgencyInfo {
  urgency: ReachUrgency;
  /** Deutschsprachiges Textlabel — die PRIMAERE, nicht-farbliche Unterscheidung
   *  (funktioniert bei Farbsehschwaeche und im Schwarzweiss-Ausdruck identisch zur
   *  Bildschirmdarstellung). Wird 1:1 auch als CSV-Spaltenwert "Dringlichkeit" genutzt. */
  label: string;
  /** Kurzes, formfeste Zweitsignal (kein Icon-Objekt, damit diese Funktion frei von
   *  React/lucide-react bleibt) — die Seite mappt `urgency` zusaetzlich auf ein Icon mit
   *  eigener Form (Dreieck/Kreis-Ausrufezeichen/Kreis-Haken/Kreis-Minus), das Symbol hier
   *  ist die textuelle Reserve fuer Kontexte ohne Icon-Rendering (z. B. Tooltips/CSV). */
  symbol: string;
}

/**
 * Stuft die Reichweite in eine von vier Dringlichkeits-Kategorien ein.
 * - `null` (kein Absatz im Zeitfenster) ist ein EIGENER, neutraler Fall — er bedeutet
 *   NICHT "unbegrenzt reicht" und darf nicht wie "dringend" wirken, aber auch nicht wie
 *   "unkritisch gruen" (Fachvorgabe Task 17: "Artikel ohne Absatz... sind ein eigener,
 *   neutraler Fall").
 * - `< REORDER_LEAD_MONTHS` (inkl. 0 = bereits leer) ⇒ kritisch.
 * - `< WARNING_HORIZON_MONTHS` ⇒ Warnung ("bald nachbestellen").
 * - sonst ⇒ ausreichend.
 */
export function classifyReachUrgency(reachMonths: number | null): ReachUrgencyInfo {
  if (reachMonths === null) {
    return { urgency: "no_sales", label: "Kein Absatz", symbol: "–" };
  }
  if (reachMonths < REORDER_LEAD_MONTHS) {
    return { urgency: "critical", label: "Kritisch", symbol: "!!!" };
  }
  if (reachMonths < WARNING_HORIZON_MONTHS) {
    return { urgency: "warning", label: "Bald nachbestellen", symbol: "!!" };
  }
  return { urgency: "ok", label: "Ausreichend", symbol: "OK" };
}

/** "5,3 Mon." / "0,0 Mon." (bereits leer) / "kein Absatz" (reach_months === null). */
export function formatReachMonths(months: number | null): string {
  if (months === null) return "kein Absatz";
  return `${months.toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} Mon.`;
}

/** Zeilen-Anzeige des Lieferanten — unterscheidet "kein Lieferant zugeordnet" (Backend
 *  liefert supplier_id NULL, wenn dr_supplier_id am Artikel fehlt/Platzhalter ist) von
 *  "Lieferant zugeordnet, aber noch unbenannt" (Backend legt neue DR-Lieferanten-IDs per
 *  Lazy-Sync mit name='' an, s. syncSuppliersFromStock in service.ts — genau der Fall, den
 *  die Inline-Pflege dieser Seite beheben soll). */
export function supplierRowLabel(supplierId: number | null, supplierName: string | null): string {
  if (supplierId === null) return "Ohne Lieferant";
  const trimmed = (supplierName ?? "").trim();
  return trimmed.length > 0 ? trimmed : "Unbenannter Lieferant";
}

/** Options-Label fuer den Lieferanten-Filter (aus useSuppliers()-Zeilen) — analoger
 *  Unbenannt-Fallback wie supplierRowLabel, zusaetzlich mit der DR-ID als Erkennungshilfe,
 *  weil in der Filterliste (anders als in der Tabellenzeile) kein Artikel danebensteht. */
export function supplierOptionLabel(supplier: Pick<Supplier, "name" | "dr_supplier_id">): string {
  const trimmed = supplier.name.trim();
  if (trimmed.length > 0) return trimmed;
  return supplier.dr_supplier_id
    ? `Unbenannt (DR-Lieferant ${supplier.dr_supplier_id})`
    : "Unbenannter Lieferant";
}

/** Korrektur #12: "Unbenannt (DR-Lieferant XXXX)"-Leichen mit 0 Grundartikeln verstopfen
 *  das Dropdown und liefern garantiert leere Ergebnisse. Nur Lieferanten mit >= 1
 *  sichtbarem Grundartikel anbieten — der aktuell AUSGEWAEHLTE bleibt immer erhalten,
 *  damit eine bestehende Auswahl nicht kommentarlos aus dem Select verschwindet.
 *  Codex-Finding E: der Pseudo-Lieferant "#AUSVERKAUF" wird ebenfalls ausgeblendet —
 *  seine Artikel sind tot-gefiltert (Task 9), die Auswahl liefert garantiert eine leere
 *  Tabelle. Gleiche Keep-if-selected-Semantik wie bei der 0-Artikel-Regel. */
export function visibleSupplierOptions(
  suppliers: Supplier[],
  currentSelection: number | "none" | null,
): Supplier[] {
  return suppliers.filter(
    (s) => (s.article_count > 0 && s.name !== "#AUSVERKAUF") || s.id === currentSelection,
  );
}

/** Korrektur #14: Zustand der Kopfzeilen-Checkbox relativ zu den GELADENEN Zeilen —
 *  Auswahl-Eintraege anderer Seiten (Blaetterung) beeinflussen den Kopf-Zustand nicht. */
export function headerSelectState(
  loadedIds: number[],
  selectedIds: ReadonlySet<number>,
): "all" | "some" | "none" {
  if (loadedIds.length === 0) return "none";
  const selectedLoaded = loadedIds.filter((id) => selectedIds.has(id)).length;
  if (selectedLoaded === 0) return "none";
  return selectedLoaded === loadedIds.length ? "all" : "some";
}

const OPEN_NOTES_TABLE_PREVIEW = 2;

function formatOneNote(note: ReachOpenNote): string {
  return note.quantity !== null ? `${note.order_number} (${note.quantity} Stk.)` : note.order_number;
}

/** Kompakte Tabellenzell-Darstellung — bei vielen offenen Vormerkungen (selten, aber
 *  moeglich bei Sammelbestellungen) wird auf `OPEN_NOTES_TABLE_PREVIEW` Eintraege
 *  gekuerzt, damit die Zeile nicht die ganze Tabelle sprengt (Fachanforderung: "Tabelle
 *  muss viele Zeilen schnell ueberblickbar machen"). */
export function summarizeOpenNotes(notes: ReachOpenNote[]): string {
  if (notes.length === 0) return "—";
  const parts = notes.map(formatOneNote);
  if (parts.length <= OPEN_NOTES_TABLE_PREVIEW) return parts.join(", ");
  const shown = parts.slice(0, OPEN_NOTES_TABLE_PREVIEW).join(", ");
  return `${shown} +${parts.length - OPEN_NOTES_TABLE_PREVIEW} weitere`;
}

/** Vollstaendige (ungekuerzte) Darstellung fuer den CSV-Export — dort zaehlt
 *  Vollstaendigkeit fuer die Weiterverarbeitung mehr als Kompaktheit. */
export function allOpenNotesText(notes: ReachOpenNote[]): string {
  if (notes.length === 0) return "";
  return notes.map(formatOneNote).join("; ");
}

// ------------------------------------------------------------
//  CSV-Export der geladenen (gefilterten) Tabellenzeilen — RFC-4180-Escaping analog
//  src/shared/services/csv.ts (Backend-Konvention: Delimiter ',', Quote '"', CRLF-
//  Zeilenenden), hier eigenstaendig im Frontend nachgebaut statt importiert, weil das
//  Frontend keinen Zugriff auf Backend-Quellcode hat (separates Vite-Bundle/tsconfig).
// ------------------------------------------------------------
interface ReachCsvColumn {
  header: string;
  get: (row: ReachRow) => string;
}

const REACH_CSV_COLUMNS: ReachCsvColumn[] = [
  { header: "Lagernummer", get: (r) => r.dr_stock_id ?? "" },
  { header: "Sternnummer", get: (r) => r.merchant_sku ?? "" },
  { header: "Name", get: (r) => r.name },
  { header: "Lieferant", get: (r) => supplierRowLabel(r.supplier_id, r.supplier_name) },
  { header: "Verkauft im Zeitfenster", get: (r) => String(r.sold_window) },
  { header: "Ø pro Monat", get: (r) => formatCsvDecimal(r.avg_month) },
  { header: "Bestand", get: (r) => String(r.quantity) },
  {
    header: "Reichweite (Monate)",
    get: (r) => (r.reach_months === null ? "" : formatCsvDecimal(r.reach_months)),
  },
  { header: "Dringlichkeit", get: (r) => classifyReachUrgency(r.reach_months).label },
  { header: "Lagerwert EK", get: (r) => formatCsvDecimal(r.stock_value_ek) },
  { header: "Offene Bestellungen", get: (r) => (r.open_purchases ?? []).map(p => `${p.order_number} (${p.qty_open} Stk.${p.eta ? `, ETA ${p.eta}` : ""})`).join("; ") },
  { header: "Alt-Vormerkungen", get: (r) => allOpenNotesText(r.open_notes ?? []) },
];

/** Baut den kompletten CSV-Text aus den aktuell GELADENEN Zeilen (Task-17-Brief: "CSV-
 *  Export der gefilterten Tabelle (Client-seitig aus geladenen rows)") — kein separater
 *  Server-Roundtrip, das entspricht bei aktiver Blaetterung der aktuell sichtbaren Seite.
 *  Reine Funktion (kein DOM-Zugriff) — der eigentliche Download-Trigger
 *  (Blob/createObjectURL) lebt bewusst in der Seite selbst, Konvention wie
 *  downloadQuarantinePayload() in pages/warehouse/LeiterListenPage.tsx. */
export function buildReachCsv(rows: ReachRow[]): string {
  const lines = [REACH_CSV_COLUMNS.map((c) => escapeCsvField(c.header)).join(",")];
  for (const row of rows) {
    lines.push(REACH_CSV_COLUMNS.map((c) => escapeCsvField(c.get(row))).join(","));
  }
  return lines.join("\r\n");
}

/** Welle 2 (#15a): automatische Tages-Sammelnummer fuer die Mengen-Schnellerfassung.
 *  order_number ist Pflichtfeld der purchase_notes-Mechanik; die Schnellerfassung ohne
 *  Dialog braucht deshalb eine deterministische Nummer je Tag. Projektkonvention:
 *  Berlin-Kalendertag (sv-SE liefert YYYY-MM-DD). Welle 4 ersetzt das durch echte Nummern. */
export function vormerkOrderNumber(now: Date = new Date()): string {
  return `VM-${now.toLocaleDateString("sv-SE", { timeZone: "Europe/Berlin" })}`;
}


// ============================================================
//  Modul 38 Stufe A — Reichweite <-> Einkaufsmodul
// ============================================================

/** Anzeige der Spalte "Offene Bestellungen" (Modul 38 A1).
 *
 *  Form: "Menge · Nr" bei einer Bestellung, bei mehreren zusaetzlich "+n weitere".
 *  Die ETA haengt separat dran, weil sie fehlen darf (kein Container terminiert).
 *  Die Liste kommt vom Server bereits nach seq sortiert (aelteste zuerst) — hier wird
 *  NICHT nachsortiert, sonst waeren es zwei Wahrheiten ueber "die aelteste".
 */
export function summarizeOpenPurchases(
  purchases: ReachOpenPurchase[] | undefined | null,
): { text: string; nummer: string; eta: string | null; order: ReachOpenPurchase | null } {
  // Der Wire-Typ garantiert das Feld — die Abfrage liefert es immer, auch leer. Der
  // Guard steht trotzdem hier, und zwar fuer genau ein Fenster: waehrend eines Deploys
  // kann ein offener Tab eine ALTE Antwort im Cache halten und schon das NEUE Bundle
  // ausfuehren. Ohne Guard stuerzt dann die ganze Tabelle ab statt einer Spalte, die
  // "—" zeigt. Eine operative Liste darf an einer Zusatzangabe nicht zerbrechen.
  if (!Array.isArray(purchases) || purchases.length === 0) {
    return { text: "—", nummer: "", eta: null, order: null };
  }
  const erste = purchases[0]!;
  const menge = purchases.reduce((n, p) => n + p.qty_open, 0);
  const rest = purchases.length - 1;
  const text = rest > 0
    ? `${menge.toLocaleString("de-DE")} · ${erste.order_number} +${rest} weitere`
    : `${menge.toLocaleString("de-DE")} · ${erste.order_number}`;
  // Naechste Ankunft ueber ALLE offenen Bestellungen des Artikels, nicht nur die erste:
  // die aelteste Bestellung muss nicht die sein, die zuerst eintrifft.
  const etas = purchases.map((p) => p.eta).filter((e): e is string => e !== null).sort();
  const nummer = rest > 0 ? `${erste.order_number} +${rest} weitere` : erste.order_number;
  return { text, nummer, eta: etas[0] ?? null, order: erste };
}

/** Alle Warnungen ueber die offenen Bestellungen eines Artikels, entdoppelt. */
export function openPurchaseWarnings(purchases: ReachOpenPurchase[] | undefined | null): string[] {
  if (!Array.isArray(purchases)) return [];
  const texte: Record<string, string> = {
    over_delivered: "mehr eingelagert als bestellt",
    unattributed_receipt: "Wareneingang ohne Positionszuordnung — Bestellung abschließen",
  };
  const gesehen = new Set<string>();
  for (const p of purchases) {
    for (const w of p.warnungen) if (texte[w]) gesehen.add(texte[w]!);
  }
  return [...gesehen];
}

/** Plan 2026-10-26 (Nutzerfall *1373): Inhalt der Spalte „Bestellt".
 *
 *  Vorn steht die Menge („280 Stk bestellt"), darunter ein Balken Bestand | unterwegs |
 *  fehlt bis zum Ziel, darunter der Status. Der Nutzer konnte „280 · 174082026174" nicht
 *  als Bestellmenge lesen, und ob sie reicht, stand nirgends.
 *
 *  Unterwegs = offene Bestellung + offene Vormerkung, wie im Backend (`offen_n`).
 *  Felder koennen bei einer alten Antwort im Cache fehlen (siehe summarizeOpenPurchases);
 *  dann gibt es die Menge, aber keinen Balken und keinen Status statt eines Absturzes.
 */
export interface BestelltZelle {
  /** null = nur Vormerkungen, und keine davon hat eine Menge. */
  menge: number | null;
  art: "bestellt" | "vorgemerkt";
  /** Zeile unter dem Kopf, wenn neben einer Bestellung Vormerkungen offen sind
   *  („+ 100 vorgemerkt", ohne bekannte Menge „+ Vormerkung"); sonst null. */
  zusatzVormerkung: string | null;
  /** Anteile 0..1 am Balken; ohne Fehlmenge fuellen Bestand + unterwegs den Balken. */
  balken: { bestand: number; unterwegs: number; fehlt: number } | null;
  status: { ton: "fehlt" | "reicht" | "luecke"; text: string }[];
  /** Ganzer Satz fuer `title`/`aria-label` des Balkens. */
  erklaerung: string;
}

export function bestelltZelle(
  row: Pick<ReachRow, "quantity" | "open_purchase_qty" | "open_notes"> &
    Partial<Pick<ReachRow, "shortfall_qty" | "reach_with_ordered_months" | "eta_gap" | "next_eta">>,
  targetMonths: number | undefined,
): BestelltZelle | null {
  const bestellt = row.open_purchase_qty ?? 0;
  const notizen = Array.isArray(row.open_notes) ? row.open_notes : [];
  if (bestellt <= 0 && notizen.length === 0) return null;
  const vorgemerkt = notizen.reduce((n, x) => n + (x.quantity ?? 0), 0);
  const mengeOffen = notizen.some((x) => x.quantity === null);
  const zahl = (n: number) => n.toLocaleString("de-DE");
  const monate = (n: number) => n.toLocaleString("de-DE", { maximumFractionDigits: 1 });
  const ziel = typeof targetMonths === "number" ? ` für ${targetMonths} Mon.` : "";

  const bestandRoh = row.quantity ?? 0;
  const unterwegs = bestellt + vorgemerkt;
  // Ein negativer Bestand (ueberverkauft) frisst zuerst die Bestellung auf, wie im Backend
  // (qty_n + offen_n). Der Balken zeigt nur, was davon wirklich uebrig bleibt.
  const bestand = Math.max(bestandRoh, 0);
  const unterwegsWirksam = Math.max(unterwegs + Math.min(bestandRoh, 0), 0);
  const neu = typeof row.shortfall_qty === "number";
  const fehlt = neu ? Math.max(row.shortfall_qty!, 0) : 0;
  const summe = bestand + unterwegsWirksam + fehlt;
  const balken = neu && summe > 0
    ? { bestand: bestand / summe, unterwegs: unterwegsWirksam / summe, fehlt: fehlt / summe }
    : null;

  const status: BestelltZelle["status"] = [];
  const mit = typeof row.reach_with_ordered_months === "number" ? row.reach_with_ordered_months : null;
  if (fehlt > 0) status.push({ ton: "fehlt", text: `fehlt ${zahl(fehlt)}${ziel}` });
  // Bei einer Ankunftsluecke waere „reicht" irrefuehrend: die Menge reicht, kommt aber zu spaet.
  else if (neu && mit !== null && row.eta_gap !== true) status.push({ ton: "reicht", text: `reicht ${monate(mit)} Mon.` });
  if (row.eta_gap === true) {
    status.push({ ton: "luecke", text: `leer vor Ankunft${row.next_eta ? ` ${formatEtaKurz(row.next_eta)}` : ""}` });
  }

  const teile = [
    `Bestand ${zahl(bestandRoh)}`,
    bestellt > 0 ? `bestellt ${zahl(bestellt)}` : null,
    vorgemerkt > 0 ? `vorgemerkt ${zahl(vorgemerkt)}` : null,
  ].filter((t): t is string => t !== null);
  if (mengeOffen) teile.push("Vormerkung ohne Menge");
  const saetze = [`${teile.join(" + ")} = ${zahl(bestandRoh + unterwegs)} Stk`];
  if (mit !== null) saetze.push(`reicht ${monate(mit)} Mon.`);
  if (fehlt > 0) saetze.push(`${ziel.trim() || "bis zum Ziel"} fehlen ${zahl(fehlt)} Stk`);
  if (row.eta_gap === true) saetze.push("Bestand ist vor der nächsten Ankunft leer");

  return {
    menge: bestellt > 0 ? bestellt : vorgemerkt > 0 ? vorgemerkt : null,
    art: bestellt > 0 ? "bestellt" : "vorgemerkt",
    zusatzVormerkung: bestellt > 0 && notizen.length > 0
      ? (vorgemerkt > 0 ? `+ ${zahl(vorgemerkt)} vorgemerkt` : "+ Vormerkung")
      : null,
    balken,
    status,
    erklaerung: saetze.join(" · "),
  };
}

/** ISO-Datum → „23.02.27": kurz genug fuer eine Zeile der 144-px-Spalte „Bestellt". */
export function formatEtaKurz(iso: string): string {
  const m = /^\d{2}(\d{2})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

/** A11: Eine Menge > 0 hakt die Zeile an, eine geleerte oder 0 hakt sie ab.
 *
 *  Reine Funktion, damit die Regel pruefbar ist, ohne die Maske zu rendern. Sie
 *  entscheidet NUR, was die Mengeneingabe mit der Auswahl macht — das Haekchen von Hand
 *  zu setzen bleibt davon unberuehrt (dafuer gibt es onToggleRow).
 *
 *  Absicht dahinter: wer eine Menge eintippt, will bestellen. Das Haekchen danach noch
 *  einzeln zu setzen, war der Schritt, den der Betrieb regelmaessig vergessen hat —
 *  die Zeile stand dann mit Menge da und war trotzdem nicht im Sammelvorgang.
 */
export function mengeWirktAufAuswahl(rohMenge: string): "anhaken" | "abhaken" | "unveraendert" {
  const t = rohMenge.trim();
  if (t === "") return "abhaken";
  const n = Number(t);
  if (!Number.isFinite(n)) return "unveraendert";
  return n > 0 ? "anhaken" : "abhaken";
}

export interface BestellArtikel {
  ga_id: number;
  sku: string;
  name: string;
  supplier_id: number | null;
  supplier_name: string | null;
  merchant_sku?: string | null;
  bausatz_teile?: ReachBausatzTeil[];
}

/** Zeilen nach Lieferant gruppieren — eine Bestellung je Lieferant (Entscheid E-4).
 *  Artikel ohne Lieferant landen in einer eigenen Gruppe; die Maske verlangt dort eine
 *  Auswahl. Kein Rateversuch über den Filter. */
export function gruppiereNachLieferant(
  zeilen: BestellArtikel[],
  mengen: Record<number, string>,
): Array<{
  supplierId: number | null;
  supplierName: string | null;
  zeilen: Array<BestellArtikel & { menge: number }>;
  mengeGesamt: number;
}> {
  const gruppen = new Map<string, {
    supplierId: number | null; supplierName: string | null;
    zeilen: Array<BestellArtikel & { menge: number }>;
    mengeGesamt: number;
  }>();
  for (const z of zeilen) {
    const menge = Number((mengen[z.ga_id] ?? "").trim());
    if (!Number.isFinite(menge) || menge <= 0) continue;
    const key = z.supplier_id === null ? "ohne" : String(z.supplier_id);
    if (!gruppen.has(key)) {
      gruppen.set(key, {
        supplierId: z.supplier_id, supplierName: z.supplier_name, zeilen: [], mengeGesamt: 0,
      });
    }
    const g = gruppen.get(key)!;
    g.zeilen.push({ ...z, menge });
    g.mengeGesamt += menge;
  }
  // Gruppen mit Lieferant zuerst, "ohne Lieferant" ans Ende — dort muss der User handeln.
  return [...gruppen.values()].sort((a, b) => {
    if ((a.supplierId === null) !== (b.supplierId === null)) return a.supplierId === null ? 1 : -1;
    return (a.supplierName ?? "").localeCompare(b.supplierName ?? "", "de");
  });
}

// ---------------------------------------------------------------------------
//  Modul 38 A8 — Panel „Offene Bestellungen" unter der Reichweiten-Liste.
// ---------------------------------------------------------------------------

/** Die Felder, die das Panel aus der Bestell-Liste (B4) braucht. */
export interface PanelBestellung {
  id: number;
  seq: number;
  order_number: string;
  status: string;
  supplier_name: string | null;
  next_eta: string | null;
  item_count?: number;
  qty_total?: number;
}

/** Sortierung des Panels: was zuerst eintrifft, steht oben.
 *
 *  Bestellungen ohne Termin ans Ende — sie sind nicht dringlicher, nur unbekannter.
 *  Bei gleichem Termin die juengere Bestellung zuerst (absteigende laufende Nummer),
 *  damit die Reihenfolge bei gleichen Daten nicht zufaellig kippt.
 */
export function sortiereOffeneBestellungen<T extends PanelBestellung>(orders: T[] | undefined | null): T[] {
  if (!Array.isArray(orders)) return [];
  return [...orders].sort((a, b) => {
    if ((a.next_eta === null) !== (b.next_eta === null)) return a.next_eta === null ? 1 : -1;
    if (a.next_eta !== null && b.next_eta !== null && a.next_eta !== b.next_eta) {
      return a.next_eta < b.next_eta ? -1 : 1;
    }
    return b.seq - a.seq;
  });
}

/** Kopfzeile des Panels: wie viele Bestellungen, Positionen und Stueck sind offen. */
export function fasseOffeneBestellungenZusammen(
  orders: PanelBestellung[] | undefined | null,
): { anzahl: number; positionen: number; menge: number } {
  if (!Array.isArray(orders)) return { anzahl: 0, positionen: 0, menge: 0 };
  return {
    anzahl: orders.length,
    // item_count/qty_total kommen erst seit B4 mit. Ein alter Antwort-Stand im Cache
    // (offener Tab waehrend eines Deploys) darf die Summe nicht zu NaN machen.
    positionen: orders.reduce((n, o) => n + (o.item_count ?? 0), 0),
    menge: orders.reduce((n, o) => n + (o.qty_total ?? 0), 0),
  };
}

/** A5 — Auswahl des Filters „Geht aus in <= X Monaten" in den API-Wert uebersetzen.
 *
 *  `null` heisst „keine Grenze". Die Auswahl ist eine feste Liste, deshalb genuegt hier
 *  die Unterscheidung „alle" gegen Zahl; alles Unerwartete faellt bewusst auf `null`
 *  zurueck (lieber zu viele Zeilen zeigen als stumm zu filtern).
 */
export function maxMonateAusWahl(wahl: string): number | null {
  if (wahl === "alle") return null;
  const n = Number(wahl);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
}
